import { Error, KeyboardArrowDown as ChevronDown } from "@mui/icons-material";
import { Dialog, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { type ElementRef, forwardRef, type ForwardRefRenderFunction, useImperativeHandle, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface ErrorDialogOptions {
    error: string;
    title?: string;
    userMessage?: string;
}

export type ErrorMethods = {
    setError: (options: ErrorDialogOptions) => void;
};

const ErrorDialog: ForwardRefRenderFunction<ErrorMethods> = (props, ref) => {
    const [errorOptions, setErrorOptions] = useState<ErrorDialogOptions | null>(null);
    const [errorVisible, setErrorVisible] = useState<boolean>(false);

    const intl = useIntl();

    useImperativeHandle(ref, () => ({
        setError(options: ErrorDialogOptions) {
            setErrorOptions(options);
            setErrorVisible(true);
        },
    }));

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    if (!errorOptions) {
        return null;
    }

    const handleClose = () => {
        setErrorVisible(false);

        setTimeout(() => {
            setErrorOptions(null); // delay cleaning error so Dialog Content does not go away while fadeout transition
        }, 200);
    };

    // Destructuring and default values
    const {
        title = intl.formatMessage({ id: "comet.errorDialog.title", defaultMessage: "Error" }),
        userMessage = intl.formatMessage({ id: "comet.errorDialog.abstractUserMessage", defaultMessage: "An error occured" }),
        error,
    } = errorOptions;

    return (
        <Dialog open={errorVisible} onClose={handleClose} fullScreen={fullScreen}>
            <DialogTitle>
                <Error />
                {title}
            </DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    {userMessage}
                    {/* TODO: maybe also show on non prod environment? */}
                    {process.env.NODE_ENV === "development" && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ChevronDown />}>
                                <FormattedMessage id="comet.errorDialog.Details" defaultMessage="Details" />
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>{error}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    <FormattedMessage id="comet.generic.ok" defaultMessage="Ok" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ForwardedErrorDialog = forwardRef(ErrorDialog);
export default ForwardedErrorDialog;

export type ErrorDialogComponentRefType = ElementRef<typeof ForwardedErrorDialog>;
